import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FolderKanban, BarChart3, Users, LogOut, Menu } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { NotificationsBell } from "@/components/NotificationsBell";
import { ROLE_LABELS, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function navItems(role) {
  const items = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/cases", label: "Cases", icon: FolderKanban },
  ];
  if (role === "people_ops" || role === "leadership") items.push({ to: "/reports", label: "Reports", icon: BarChart3 });
  if (role === "leadership") items.push({ to: "/admin", label: "People & Users", icon: Users });
  return items;
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = navItems(user.role);

  const NavLinks = ({ onClick }) => (
    <nav className="flex flex-col gap-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onClick}
          data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors",
              isActive ? "bg-patria-green text-patria-surface" : "text-patria-ink hover:bg-patria-sage"
            )
          }
        >
          <it.icon className="h-4 w-4" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-patria-sage">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-patria-surface border-b border-patria-muted/20">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-toggle">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2.5 min-w-0">
              <img src="/patria-logo.png" alt="Patria Church" className="h-10 w-10 rounded-full object-contain shrink-0" />
              <div className="min-w-0">
                <p className="font-heading font-semibold text-patria-ink leading-tight truncate">COMPASS</p>
                <p className="text-[11px] text-patria-muted leading-tight truncate hidden sm:block">Patria Church Performance Management</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationsBell />
            <div className="flex items-center gap-2.5 pl-1 sm:pl-2 border-l border-patria-muted/20">
              <div className="h-8 w-8 rounded-full bg-patria-terracotta/15 text-patria-terracottaDeep flex items-center justify-center text-xs font-semibold" data-testid="user-avatar">
                {initials(user.name)}
              </div>
              <div className="hidden sm:block leading-tight">
                <p className="text-sm font-medium text-patria-ink" data-testid="user-name">{user.name}</p>
                <p className="text-[11px] text-patria-muted" data-testid="user-role">{ROLE_LABELS[user.role]}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button" title="Sign out">
                <LogOut className="h-4 w-4 text-patria-muted" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-6 flex gap-6">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-24">
            <NavLinks />
          </div>
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-30 lg:hidden" onClick={() => setMobileOpen(false)}>
            <div className="absolute inset-0 bg-patria-ink/30" />
            <div className="absolute left-0 top-16 bottom-0 w-64 bg-patria-surface p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <NavLinks onClick={() => setMobileOpen(false)} />
            </div>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0 fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
